import { useQuery } from '@tanstack/react-query';
import { get } from '../../lib/api.js';

export interface Member {
  id: string;
  userId: string;
  displayName: string;
  phoneE164: string | null;
  employeeCode: string | null;
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'EXITED';
  joinedAt: string;
  exitedAt: string | null;
  teamId: string | null;
  managerId: string | null;
  role: { id: string; key: string; name: string };
  team: { id: string; name: string } | null;
  user: { email: string; lastLoginAt: string | null; totpEnabledAt: string | null };
}

export interface Team {
  id: string;
  name: string;
  leadId: string | null;
  lead: { id: string; displayName: string } | null;
  _count: { members: number };
}

export type Scope = 'OWN' | 'TEAM' | 'PROJECT' | 'ALL';

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  requires2fa: boolean;
  /** Stored field policies (defaults filled in) and what they mean with the role's permissions. */
  fieldPolicies: Record<string, FieldPolicy>;
  effectiveFieldPolicies: Record<string, FieldPolicy>;
  exportRowLimit: number;
  ipAllowList: string[];
  permissions: { permission: string; scope: Scope }[];
  _count: { memberships: number };
}

export type FieldPolicy = 'visible' | 'masked' | 'hidden';

export interface FieldPolicyDef {
  key: string;
  label: string;
  description: string;
  kind: 'phone' | 'email';
  revealPermission: string;
}

export interface ExportLogEntry {
  id: string;
  dataset: string;
  format: string;
  filters: Record<string, unknown>;
  status: 'COMPLETED' | 'REFUSED';
  rowCount: number;
  rowLimit: number;
  maskedFields: string[];
  reason: string | null;
  ip: string | null;
  createdAt: string;
  member: { id: string; displayName: string; role: { name: string } };
}

export interface PermissionDef {
  key: string;
  label: string;
  group: string;
  scoped?: boolean;
}

export const useMembers = (status?: string) =>
  useQuery({
    queryKey: ['members', status ?? 'current'],
    queryFn: () => get<Member[]>(`/members${status ? `?status=${status}` : ''}`),
  });
export const useTeams = () => useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') });
export const useRoles = () => useQuery({ queryKey: ['roles'], queryFn: () => get<Role[]>('/roles') });
export const usePermissionCatalog = () =>
  useQuery({
    queryKey: ['permission-catalog'],
    queryFn: () => get<PermissionDef[]>('/permissions'),
    staleTime: Infinity,
  });

export const useFieldPolicyCatalog = () =>
  useQuery({
    queryKey: ['field-policy-catalog'],
    queryFn: () => get<FieldPolicyDef[]>('/field-policies'),
    staleTime: Infinity,
  });
export const useExportLog = (filter: { status?: string; dataset?: string }) =>
  useQuery({
    queryKey: ['export-logs', filter],
    queryFn: () => {
      const qs = new URLSearchParams({ take: '100' });
      if (filter.status) qs.set('status', filter.status);
      if (filter.dataset) qs.set('dataset', filter.dataset);
      return get<ExportLogEntry[]>(`/export-logs?${qs.toString()}`);
    },
  });

export const STATUS_TONE = {
  INVITED: 'info',
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  EXITED: 'neutral',
} as const;

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}
